/*
  Fuego & Fragancia — carrusel perpetuo de videos / fotos.

  Se mueve SIEMPRE hacia la izquierda, sin principio ni final visibles, igual
  que el marquee del anuncio superior. La velocidad se expresa en SEGUNDOS POR
  VUELTA (cuánto tarda en pasar una tanda completa de tarjetas), no en píxeles:
  así el ajuste del editor significa lo mismo tanto si hay 3 tarjetas como si
  hay 12, y coincide con la unidad que ya usa el marquee.

  Por qué transform y no scrollLeft (que es lo que hacía antes):
  el scroll programático pelea con `scroll-snap`, con el arrastre del dedo y con
  la inercia del móvil, y el navegador lo repinta en el hilo principal. Un
  translate3d lo maneja el compositor: se ve fluido y no da tirones.
*/
if (!customElements.get('ff-video-carousel')) {
  customElements.define(
    'ff-video-carousel',
    class FfVideoCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.ff-video-carousel__track');
        this.originalSet = this.querySelector('.ff-video-carousel__set');
        this.viewport = this.querySelector('.ff-video-carousel__viewport');
        if (!this.track || !this.originalSet || !this.viewport) return;

        this.lapSeconds = parseFloat(this.dataset.lap) || 26;
        this.autoplay = this.dataset.autoplay !== 'false';

        /*
          OJO: por defecto NO se respeta `prefers-reduced-motion`. Ya pasó tres
          veces en este tema (marquee, nieve y este mismo carrusel): el equipo
          tiene activado "reducir movimiento" en su sistema, así que la
          animación se apagaba sola y parecía que el ajuste no servía. Quien
          quiera el comportamiento accesible lo enciende desde el editor.
        */
        const respectReducedMotion = this.dataset.respectReducedMotion === 'true';
        if (respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.autoplay = false;
        }

        this.offset = 0;
        this.setWidth = 0;
        this.paused = false;
        this.videoPlaying = false;
        this.tween = null;

        this.fill();
        this.bindPause();
        this.bindArrows();
        this.bindDrag();
        this.bindLightbox();

        /*
          Las imágenes son lazy: el ancho real de una tanda no se conoce hasta
          que cargan. Antes aquí solo se volvía a MEDIR, y eso no bastaba.

          Medir corrige el punto de corte del bucle, pero no el número de
          copias. Al montar, con las fotos aún sin cargar, la tanda mide muy
          poco y se clona lo justo para ese ancho falso; cuando las fotos
          entran, la tanda crece, las copias se quedan cortas y aparecen huecos
          vacíos al dar la vuelta. Con tarjetas de ancho variable —el modo
          nuevo— el desfase es todavía mayor.

          Ahora se vuelve a rellenar, pero solo si el ancho cambió de verdad:
          `fill()` toca el riel y sin ese filtro el observador se
          retroalimentaría en bucle.
        */
        this.resizeObserver = new ResizeObserver(() => {
          const antes = this.setWidth;
          this.measure();
          if (Math.abs(this.setWidth - antes) > 1) {
            cancelAnimationFrame(this._refill);
            this._refill = requestAnimationFrame(() => this.fill());
          }
        });
        this.resizeObserver.observe(this.originalSet);

        this.lastFrame = 0;
        this.raf = requestAnimationFrame(this.tick.bind(this));
      }

      /* ---------- Montaje ---------- */

      // Clona la tanda original hasta que el riel mida al menos el doble del
      // ancho visible. Con pocas tarjetas, dos tandas no alcanzan a cubrir una
      // pantalla ancha y se abriría un hueco vacío al dar la vuelta.
      fill() {
        this.track.querySelectorAll('[data-ff-clone]').forEach((node) => node.remove());
        this.measure();

        const needed = Math.max(this.viewport.clientWidth * 2, 1);
        let guard = 0;
        while (this.track.scrollWidth < needed && this.setWidth > 0 && guard < 12) {
          const clone = this.originalSet.cloneNode(true);
          clone.setAttribute('data-ff-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          // Los ids duplicados confunden a los lectores de pantalla y a
          // deferred-media de Dawn, que busca su plantilla por id.
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          clone.querySelectorAll('a, button, video').forEach((node) => node.setAttribute('tabindex', '-1'));
          this.track.appendChild(clone);
          guard += 1;
        }

        // Con una sola tanda el bucle no tendría nada que mostrar tras el corte.
        if (this.track.children.length < 2 && this.setWidth > 0) {
          const clone = this.originalSet.cloneNode(true);
          clone.setAttribute('data-ff-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          this.track.appendChild(clone);
        }
      }

      measure() {
        this.setWidth = this.originalSet.getBoundingClientRect().width;
      }

      /* ---------- Pausas ---------- */

      bindPause() {
        this.addEventListener('pointerenter', () => (this.hovering = true));
        this.addEventListener('pointerleave', () => (this.hovering = false));
        this.addEventListener('focusin', () => (this.hovering = true));
        this.addEventListener('focusout', () => (this.hovering = false));

        // En táctil no existe pointerleave: sin temporizador el carrusel se
        // quedaba congelado para siempre tras el primer toque.
        this.addEventListener('touchstart', () => this.pauseTemporarily(), { passive: true });

        // Mientras un testimonio se reproduce, mover el riel es insufrible.
        this.addEventListener('play', () => (this.videoPlaying = true), true);
        this.addEventListener('pause', () => (this.videoPlaying = false), true);
        this.addEventListener('ended', () => (this.videoPlaying = false), true);
      }

      pauseTemporarily(delay = 4000) {
        this.paused = true;
        clearTimeout(this._resumeTimeout);
        this._resumeTimeout = setTimeout(() => (this.paused = false), delay);
      }

      get isMoving() {
        return this.autoplay && !this.paused && !this.hovering && !this.videoPlaying && !this.dragging;
      }

      /* ---------- Flechas ---------- */

      bindArrows() {
        this.querySelectorAll('.ff-video-carousel__arrow').forEach((btn) => {
          btn.addEventListener('click', () => {
            const dir = parseInt(btn.dataset.direction, 10) || 1;
            const card = this.originalSet.querySelector('.ff-video-carousel__card');
            const gap = parseFloat(getComputedStyle(this.originalSet).columnGap) || 0;
            const step = card ? card.getBoundingClientRect().width + gap : 240;
            this.startTween(this.offset + dir * step);
            this.pauseTemporarily();
          });
        });
      }

      startTween(to) {
        this.tween = { from: this.offset, to, start: performance.now(), duration: 420 };
      }

      /* ---------- Arrastre ---------- */

      bindDrag() {
        // Los <a> y las <img> se arrastran de forma nativa: sin esto el
        // navegador secuestra el gesto y muestra la imagen fantasma.
        this.track.addEventListener('dragstart', (event) => event.preventDefault());

        this.viewport.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          this.pointerId = event.pointerId;
          this.startX = event.clientX;
          this.startOffset = this.offset;
          this.dragging = false;
          this.tween = null;
        });

        this.viewport.addEventListener('pointermove', (event) => {
          if (this.pointerId !== event.pointerId || this.startX == null) return;
          const dx = event.clientX - this.startX;

          // La captura se difiere hasta que el gesto es un arrastre de verdad.
          // Si se capturara en pointerdown, ningún clic llegaría a los botones
          // de reproducir.
          if (!this.dragging && Math.abs(dx) > 4) {
            this.dragging = true;
            this.classList.add('is-dragging');
            this.viewport.setPointerCapture(event.pointerId);
          }
          if (this.dragging) this.offset = this.startOffset - dx;
        });

        const end = () => {
          if (this.dragging) {
            this.classList.remove('is-dragging');
            /*
              Al soltar tras arrastrar, el navegador dispara igualmente un
              click. Sin esta marca, cada arrastre terminaria abriendo la
              ampliacion de la tarjeta que quedara bajo el dedo. Se limpia en
              el siguiente tick, ya pasado ese click fantasma.
            */
            this.justDragged = true;
            setTimeout(() => (this.justDragged = false), 0);
          }
          this.startX = null;
          this.pointerId = null;
          // Se deja un respiro antes de retomar la marcha para que no arranque
          // de golpe en cuanto se suelta el dedo.
          this.dragging = false;
          this.pauseTemporarily(2000);
        };
        this.viewport.addEventListener('pointerup', end);
        this.viewport.addEventListener('pointercancel', end);
      }

      /* ---------- Ampliacion ----------

         Se escucha en fase de CAPTURA sobre el riel, y hay motivo: el boton
         del poster de un video lleva su propio listener (deferred-media de
         Dawn, que carga el video dentro de la tarjeta). Capturando antes y
         cortando la propagacion, el clic abre la ampliacion en vez de
         reproducirlo en miniatura.

         El indice sale de la posicion DENTRO de su tanda, no del riel entero:
         las copias del bucle tienen los mismos hijos en el mismo orden, asi
         que pulsar la tercera tarjeta de cualquier copia abre la tercera
         original. Sin esto, las copias abririan fotos equivocadas.
      */
      bindLightbox() {
        if (this.dataset.lightbox !== 'true') return;

        this.track.addEventListener(
          'click',
          (event) => {
            if (this.justDragged) return;
            const card = event.target.closest('.ff-video-carousel__card');
            if (!card || !this.track.contains(card)) return;

            const set = card.parentElement;
            const indice = Array.prototype.indexOf.call(set.children, card);
            if (indice < 0) return;

            event.preventDefault();
            event.stopPropagation();
            this.abrir(indice);
          },
          true
        );
      }

      get originales() {
        return Array.prototype.slice.call(this.originalSet.children);
      }

      abrir(indice) {
        this.indice = indice;
        if (!this.capa) this.crearCapa();

        this.pintar();
        document.body.appendChild(this.capa);
        requestAnimationFrame(() => this.capa.classList.add('is-open'));

        this.alTeclado = (e) => {
          if (e.key === 'Escape') this.cerrar();
          if (e.key === 'ArrowRight') this.mover(1);
          if (e.key === 'ArrowLeft') this.mover(-1);
        };
        document.addEventListener('keydown', this.alTeclado);

        // La tira se queda quieta mientras se mira una foto en grande.
        this.paused = true;
        this.scrollPrevio = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }

      crearCapa() {
        const capa = document.createElement('div');
        capa.className = 'ff-vc-lb';
        capa.setAttribute('role', 'dialog');
        capa.setAttribute('aria-modal', 'true');

        const figura = document.createElement('figure');
        figura.className = 'ff-vc-lb__figure';

        const medio = document.createElement('div');
        medio.className = 'ff-vc-lb__media';
        figura.appendChild(medio);

        const cuerpo = document.createElement('figcaption');
        cuerpo.className = 'ff-vc-lb__body';
        const nombre = document.createElement('span');
        nombre.className = 'ff-vc-lb__name';
        const etiqueta = document.createElement('span');
        etiqueta.className = 'ff-vc-lb__label';
        cuerpo.appendChild(nombre);
        cuerpo.appendChild(etiqueta);
        figura.appendChild(cuerpo);

        capa.appendChild(figura);

        const cerrar = this.botonCapa('ff-vc-lb__close', 'x', this.dataset.closeLabel || 'Close');
        cerrar.addEventListener('click', () => this.cerrar());
        capa.appendChild(cerrar);

        if (this.originales.length > 1) {
          const ant = this.botonCapa('ff-vc-lb__prev', '<', 'Anterior');
          ant.addEventListener('click', () => this.mover(-1));
          capa.appendChild(ant);

          const sig = this.botonCapa('ff-vc-lb__next', '>', 'Siguiente');
          sig.addEventListener('click', () => this.mover(1));
          capa.appendChild(sig);
        }

        // Pulsar el fondo cierra; pulsar la foto o el video, no.
        capa.addEventListener('click', (e) => {
          if (e.target === capa) this.cerrar();
        });

        this.capa = capa;
        this.capaMedio = medio;
        this.capaNombre = nombre;
        this.capaEtiqueta = etiqueta;
      }

      botonCapa(clase, signo, etiqueta) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ff-vc-lb__btn ' + clase;
        b.setAttribute('aria-label', etiqueta);
        b.textContent = signo;
        return b;
      }

      pintar() {
        const card = this.originales[this.indice];
        if (!card) return;

        this.capaMedio.textContent = '';

        if (card.dataset.lbType === 'video') {
          /*
            Se clona el <template> que deferred-media ya trae, en vez de armar
            un <video> a mano: asi se usan las fuentes y el poster que genera
            Shopify, y si manana cambia ese markup, esto lo sigue solo.
          */
          const plantilla = card.querySelector('template');
          if (plantilla) {
            this.capaMedio.appendChild(plantilla.content.cloneNode(true));
            const video = this.capaMedio.querySelector('video');
            if (video) {
              video.setAttribute('controls', '');
              video.removeAttribute('loop');
              video.classList.add('ff-vc-lb__video');
            }
          }
        } else if (card.dataset.lbSrc) {
          const img = document.createElement('img');
          img.className = 'ff-vc-lb__img';
          img.src = card.dataset.lbSrc;
          img.alt = card.dataset.lbName || '';
          this.capaMedio.appendChild(img);
        }

        const nombre = card.dataset.lbName || '';
        const etiqueta = card.dataset.lbLabel || '';
        this.capaNombre.textContent = nombre;
        this.capaEtiqueta.textContent = etiqueta;
        this.capaNombre.style.display = nombre ? '' : 'none';
        this.capaEtiqueta.style.display = etiqueta ? '' : 'none';
      }

      mover(paso) {
        const total = this.originales.length;
        if (!total) return;
        // Resto doble: al retroceder desde la primera da la vuelta al final,
        // que es lo que se espera. Un resto normal devolveria negativo.
        this.indice = (((this.indice + paso) % total) + total) % total;
        this.pintar();
      }

      cerrar() {
        if (!this.capa || !this.capa.parentNode) return;

        // Se para el video antes de quitar la capa: si no, sigue sonando.
        const video = this.capa.querySelector('video');
        if (video) video.pause();

        this.capa.classList.remove('is-open');
        document.removeEventListener('keydown', this.alTeclado);
        document.body.style.overflow = this.scrollPrevio || '';
        this.paused = false;

        const capa = this.capa;
        setTimeout(() => {
          if (capa.parentNode) capa.parentNode.removeChild(capa);
        }, 280);
      }

      /* ---------- Bucle ---------- */

      tick(now) {
        const delta = this.lastFrame ? (now - this.lastFrame) / 1000 : 0;
        this.lastFrame = now;

        if (this.tween) {
          const elapsed = (now - this.tween.start) / this.tween.duration;
          if (elapsed >= 1) {
            this.offset = this.tween.to;
            this.tween = null;
          } else {
            const eased = 1 - Math.pow(1 - elapsed, 3);
            this.offset = this.tween.from + (this.tween.to - this.tween.from) * eased;
          }
        } else if (this.isMoving && this.setWidth > 0) {
          // delta en segundos evita que la velocidad dependa de los FPS del
          // dispositivo: 26 segundos por vuelta son 26 segundos en todas partes.
          this.offset += (this.setWidth / this.lapSeconds) * Math.min(delta, 0.1);
        }

        if (this.setWidth > 0) {
          // Módulo tolerante a negativos: al arrastrar hacia atrás el offset se
          // vuelve negativo y un `%` normal devolvería un resto negativo.
          this.offset = ((this.offset % this.setWidth) + this.setWidth) % this.setWidth;
        }

        this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
        this.raf = requestAnimationFrame(this.tick.bind(this));
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        clearTimeout(this._resumeTimeout);
      }
    }
  );
}
